import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, PrimaryButton } from '@/components/Card';
import { Chip, ChipRow, SelectableChip } from '@/components/ChipRow';
import { api } from '@/lib/api';
import type { CalendarCandidate, CalendarStatus } from '@shared/types';

function chipLabel(event: CalendarCandidate): string {
  const route =
    event.originIata && event.destIata ? `${event.originIata} → ${event.destIata}` : event.originIata || event.destIata || 'airports unknown';
  const flight = event.flightNumber || event.airline || 'flight';
  const when = event.departAt ? event.departAt.slice(0, 16).replace('T', ' ') : 'no time on event';
  return `${flight} · ${route} · ${when}`;
}

export function CalendarImportPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [status, setStatus] = useState<CalendarStatus | null>(null);
  const [candidates, setCandidates] = useState<CalendarCandidate[]>([]);
  const [mode, setMode] = useState<'oauth' | 'demo' | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const oauthError = params.get('oauth') === 'error' ? params.get('reason') : null;

  async function loadStatus() {
    const next = await api.calendarStatus();
    setStatus(next);
    setNotice(next.notice);
    return next;
  }

  async function scan() {
    setBusy(true);
    setError(null);
    try {
      const data = await api.calendarScan();
      setMode(data.mode);
      setNotice(data.notice);
      setCandidates(data.candidates);
      setSelected(data.candidates.filter((row) => row.importable && !row.alreadyImported).map((row) => row.eventId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Scan failed.');
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    loadStatus()
      .then(() => scan())
      .catch((err) => setError(err instanceof Error ? err.message : 'Calendar status failed.'));
  }, []);

  const importableSelected = useMemo(
    () => selected.filter((id) => candidates.some((row) => row.eventId === id && row.importable && !row.alreadyImported)),
    [selected, candidates],
  );

  function toggle(event: CalendarCandidate) {
    if (!event.importable || event.alreadyImported) return;
    setSelected((cur) => (cur.includes(event.eventId) ? cur.filter((id) => id !== event.eventId) : [...cur, event.eventId]));
  }

  async function connect() {
    setBusy(true);
    setError(null);
    try {
      const data = await api.calendarConnect();
      window.location.assign(data.url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start Google OAuth.');
      setBusy(false);
    }
  }

  async function disconnect() {
    await api.calendarDisconnect();
    setSelected([]);
    await loadStatus();
    await scan();
  }

  async function importSelected() {
    if (!importableSelected.length) return;
    setBusy(true);
    setError(null);
    try {
      const data = await api.calendarImport(importableSelected);
      if (data.trips[0]) {
        navigate(`/trips/${data.trips[0].trip.id}`);
        return;
      }
      await scan();
      setNotice(data.notice);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <Card className="space-y-3">
        <h2 className="font-display text-[30px] leading-none text-navy">Import from Google Calendar</h2>
        <ChipRow>
          <Chip tone={mode === 'demo' ? 'brass' : 'navy'}>{mode === 'demo' ? 'Demo scan' : 'Calendar'}</Chip>
          {status?.connected && <Chip tone="success">Connected</Chip>}
          {status?.oauthConfigured ? <Chip>OAuth ready</Chip> : <Chip tone="alert">OAuth secrets missing</Chip>}
          {status?.householdShared && <Chip>Household shared</Chip>}
        </ChipRow>
        <p className="text-sm text-muted">{notice}</p>
        {status?.calendarEmail && <p className="text-[13px] text-muted">Signed in as {status.calendarEmail}</p>}
        {oauthError && <p className="text-sm text-alert">OAuth did not finish ({oauthError}).</p>}
        <p className="text-[12px] text-faint">
          Read-only scope. Chip’s life HQ calendar is chipwilkes@gmail.com — connect that Google account. We parse the event; we do not invent gates or live times.
        </p>
        <div className="grid grid-cols-2 gap-2">
          {status?.oauthConfigured && !status.connected && (
            <PrimaryButton disabled={busy} onClick={connect} className="col-span-2">
              Connect Google Calendar
            </PrimaryButton>
          )}
          <button
            type="button"
            className="rounded-card border border-line px-3 py-2.5 text-sm font-semibold"
            disabled={busy}
            onClick={scan}
          >
            Scan next 90 days
          </button>
          {status?.connected ? (
            <button
              type="button"
              className="rounded-card border border-line px-3 py-2.5 text-sm font-semibold"
              disabled={busy}
              onClick={disconnect}
            >
              Disconnect
            </button>
          ) : (
            <Link to="/trips" className="rounded-card border border-line px-3 py-2.5 text-center text-sm font-semibold">
              Back to trips
            </Link>
          )}
        </div>
      </Card>

      <Card className="space-y-3">
        <h3 className="font-display text-2xl text-navy">Flight-like events</h3>
        <p className="text-sm text-muted">Multi-select chips wrap. Events without both airports stay visible but cannot import.</p>
        {candidates.length === 0 && <p className="text-sm text-muted">No flight-like events in the window.</p>}
        <ChipRow>
          {candidates.map((event) => (
            <SelectableChip
              key={event.eventId}
              selected={selected.includes(event.eventId)}
              disabled={!event.importable || event.alreadyImported}
              onClick={() => toggle(event)}
            >
              {event.alreadyImported ? 'Imported · ' : ''}
              {!event.importable ? 'Incomplete · ' : ''}
              {chipLabel(event)}
            </SelectableChip>
          ))}
        </ChipRow>
        <PrimaryButton disabled={busy || importableSelected.length === 0} onClick={importSelected}>
          Import {importableSelected.length || 0} selected
        </PrimaryButton>
        {error && <p className="text-sm text-alert">{error}</p>}
      </Card>

      {candidates.map((event) => (
        <Card key={`${event.eventId}-detail`}>
          <ChipRow>
            {event.demo && <Chip tone="brass">Demo payload</Chip>}
            {event.alreadyImported && <Chip tone="success">Already imported</Chip>}
            {event.importable ? <Chip tone="navy">Importable</Chip> : <Chip tone="alert">Needs airports</Chip>}
            {event.airline && <Chip>{event.airline}</Chip>}
            {event.flightNumber && <Chip>{event.flightNumber}</Chip>}
          </ChipRow>
          <p className="mt-2 text-sm font-semibold">{event.title}</p>
          <p className="text-[13px] text-muted">{event.location || 'No location on event'}</p>
          <ul className="mt-2 list-disc space-y-1 pl-4 text-[12px] text-muted">
            {event.parseNotes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </Card>
      ))}
    </div>
  );
}
